import classNames from "classnames";
import React, { FC, ReactNode, useMemo, useState } from "react";
import {
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuInfo,
  LuRepeat,
  LuX,
  LuZap,
} from "react-icons/lu";

import { StyledDatePicker } from "../../../../components/datepicker/datepicker";
import { Button } from "../../../../components/newUI/button";
import { InfoTip } from "../../../settings/team/editUserParts";
import {
  buildRepeatPayload,
  completionPeriodsLater,
  CreateMode,
  DAY_OF_MONTH_OPTIONS,
  DayOfMonth,
  Frequency,
  getDefaultForm,
  getDefaultRepeatUntil,
  MONTH_NAMES,
  MONTHLY_EVERY_OPTIONS,
  ordinal,
  RecurringScheduleForm,
  RepeatWorkPayload,
  ScheduleWork,
  WEEKDAY_LETTERS,
} from "./recurringSchedule";

type Props = {
  work: ScheduleWork;
  /** Pre-filled settings when editing a running schedule. */
  initialForm?: RecurringScheduleForm;
  /** New work window: the schedule isn't running yet, so it's not an edit. */
  isDraft?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onSave: (payload: RepeatWorkPayload, form: RecurringScheduleForm) => void;
};

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: "never", label: "Never" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const CREATE_MODES: { value: CreateMode; label: string; icon: ReactNode }[] = [
  { value: "immediately", label: "Immediately", icon: <LuZap size={16} /> },
  {
    value: "specific",
    label: "On specific day",
    icon: <LuCalendar size={16} />,
  },
  { value: "completion", label: "On completion", icon: <LuCheck size={16} /> },
];

const QUARTER_MONTH_OPTIONS = [0, 1, 2].map((value) => ({
  value,
  label: `${ordinal(value + 1)} month`,
}));

const SELECT_CLASSES =
  "w-full cursor-pointer appearance-none rounded-lg border border-[#DCE2EA] bg-white text-[13.5px] text-[#1F2433] outline-none transition-colors focus:border-[#6B4EFF]";
const INLINE_TEXT_CLASSES = "text-[13px] text-[#5B6678]";

type SelectProps<T extends string | number> = {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  large?: boolean;
  disabled?: boolean;
};

const Select = <T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
  large,
  disabled,
}: SelectProps<T>) => (
  <div className={classNames("relative", className)}>
    <select
      aria-label={ariaLabel}
      value={String(value)}
      disabled={disabled}
      onChange={(event) => {
        const option = options.find(
          (item) => String(item.value) === event.target.value,
        );
        if (option) onChange(option.value);
      }}
      className={classNames(
        SELECT_CLASSES,
        large ? "h-[38px] py-[9px] pl-3 pr-8" : "h-9 py-2 pl-[11px] pr-[26px]",
        disabled &&
          "cursor-not-allowed !border-[#E4E7EC] !bg-[#F2F3F6] !text-[#8A90A2]",
      )}>
      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
    <LuChevronDown
      size={14}
      className={
        "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6B7185]"
      }
    />
  </div>
);

const Row: FC<{
  label: string;
  info?: string;
  first?: boolean;
  children: ReactNode;
}> = ({ label, info, first, children }) => (
  <div
    className={classNames(
      "grid grid-cols-[110px_1fr]",
      first ? "pb-3.5" : "border-t border-[#EDEFF3] py-3.5",
    )}>
    <div
      className={
        "flex items-start gap-1.5 pt-[9px] text-[13px] text-[#3A4256]"
      }>
      {label}
      {info && (
        <span
          title={info}
          aria-label={info}
          className={"mt-px cursor-help text-[#9097A8]"}>
          <LuInfo size={14} />
        </span>
      )}
    </div>
    <div className={"min-w-0"}>{children}</div>
  </div>
);

const WeekdayPicker: FC<{
  value: number;
  onChange: (value: number) => void;
  ariaLabel: string;
}> = ({ value, onChange, ariaLabel }) => (
  <div role={"radiogroup"} aria-label={ariaLabel} className={"flex gap-1"}>
    {WEEKDAY_LETTERS.map((letter, index) => (
      <button
        key={index}
        type={"button"}
        role={"radio"}
        aria-checked={value === index}
        aria-label={
          [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ][index]
        }
        onClick={() => onChange(index)}
        className={classNames(
          "flex h-[26px] w-[26px] items-center justify-center rounded-full border text-[11px] transition-colors",
          value === index
            ? "border-[#6B4EFF] bg-[#6B4EFF] font-bold text-white"
            : "border-[#DCE2EA] bg-white font-medium text-[#5B6678] hover:border-[#6B4EFF]",
        )}>
        {letter}
      </button>
    ))}
  </div>
);

/** A greyed-out box showing the only choice "On completion" allows. */
const FixedSelect: FC<{
  label: string;
  ariaLabel: string;
  className?: string;
  large?: boolean;
}> = ({ label, ariaLabel, className, large }) => (
  <Select
    disabled
    large={large}
    ariaLabel={ariaLabel}
    className={className}
    value={"fixed"}
    options={[{ value: "fixed", label }]}
    onChange={() => undefined}
  />
);

const dayOptions = DAY_OF_MONTH_OPTIONS as {
  value: DayOfMonth;
  label: string;
}[];
const monthOptions = MONTH_NAMES.map((label, value) => ({ value, label }));

export const RecurringScheduleModal: FC<Props> = ({
  work,
  initialForm,
  isDraft,
  isLoading,
  onClose,
  onSave,
}) => {
  const isEditing = !!initialForm && !isDraft;
  const [form, setForm] = useState<RecurringScheduleForm>(
    () => initialForm ?? getDefaultForm(work),
  );
  const [isRepeatUntilEdited, setIsRepeatUntilEdited] = useState(isEditing);
  const update = (changes: Partial<RecurringScheduleForm>) =>
    setForm((current) => {
      const next = { ...current, ...changes };
      // Until the user picks an end date, keep it long enough for the frequency.
      if (changes.frequency && !isRepeatUntilEdited) {
        next.repeatUntil = getDefaultRepeatUntil(changes.frequency);
      }
      return next;
    });

  const result = useMemo(() => buildRepeatPayload(form, work), [form, work]);
  const isRepeating = form.frequency !== "never";
  const isWeekly = form.frequency === "weekly" || form.frequency === "biweekly";
  const isCompletion = form.createMode === "completion";
  const completionPeriods = completionPeriodsLater(form);
  const [isInvalidDueDateOpen, setIsInvalidDueDateOpen] = useState(false);

  const renderCreateDetails = () => {
    if (isCompletion) return null;

    if (isWeekly) {
      return (
        <div className={"mt-2.5"}>
          <WeekdayPicker
            ariaLabel={"Day the work is created"}
            value={form.createWeekday}
            onChange={(createWeekday) => update({ createWeekday })}
          />
        </div>
      );
    }

    if (form.frequency === "monthly") {
      return (
        <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
          <span className={INLINE_TEXT_CLASSES}>Start date is on the</span>
          <Select
            ariaLabel={"Start day of the month"}
            className={"w-24"}
            value={form.createDay}
            options={dayOptions}
            onChange={(createDay) => update({ createDay })}
          />
          <span className={INLINE_TEXT_CLASSES}>of each month</span>
        </div>
      );
    }

    if (form.frequency === "quarterly") {
      return (
        <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
          <span className={INLINE_TEXT_CLASSES}>on the</span>
          <Select
            ariaLabel={"Start day"}
            className={"w-24"}
            value={form.createDay}
            options={dayOptions}
            onChange={(createDay) => update({ createDay })}
          />
          <span className={INLINE_TEXT_CLASSES}>of the</span>
          <Select
            ariaLabel={"Start month of the quarter"}
            className={"w-[120px]"}
            value={form.createQuarterMonth}
            options={QUARTER_MONTH_OPTIONS}
            onChange={(createQuarterMonth) => update({ createQuarterMonth })}
          />
        </div>
      );
    }

    return (
      <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
        <span className={INLINE_TEXT_CLASSES}>On</span>
        <Select
          ariaLabel={"Start month"}
          className={"w-[140px]"}
          value={form.createMonth}
          options={monthOptions}
          onChange={(createMonth) => update({ createMonth })}
        />
        <Select
          ariaLabel={"Start day"}
          className={"w-24"}
          value={form.createDay}
          options={dayOptions}
          onChange={(createDay) => update({ createDay })}
        />
      </div>
    );
  };

  const renderDue = () => {
    if (isWeekly) {
      return (
        <div className={"flex flex-col gap-2.5"}>
          <WeekdayPicker
            ariaLabel={"Due day"}
            value={form.dueWeekday}
            onChange={(dueWeekday) => update({ dueWeekday })}
          />
          {isCompletion ? (
            <FixedSelect
              large
              ariaLabel={"Due week"}
              label={
                completionPeriods > 1
                  ? `${completionPeriods} weeks later`
                  : "of the following week"
              }
            />
          ) : (
            <Select
              large
              ariaLabel={"Due week"}
              value={form.dueWeekOffset}
              options={[
                { value: "same", label: "of the same week" },
                { value: "following", label: "of the following week" },
              ]}
              onChange={(dueWeekOffset) =>
                update({
                  dueWeekOffset: dueWeekOffset as "same" | "following",
                })
              }
            />
          )}
        </div>
      );
    }

    if (form.frequency === "monthly") {
      return (
        <div className={"flex flex-wrap items-center gap-2"}>
          <span className={INLINE_TEXT_CLASSES}>on the</span>
          <Select
            ariaLabel={"Due day of the month"}
            className={"w-24"}
            value={form.dueDay}
            options={dayOptions}
            onChange={(dueDay) => update({ dueDay })}
          />
          {isCompletion ? (
            <FixedSelect
              ariaLabel={"Due month"}
              className={"w-[200px]"}
              label={
                completionPeriods > 1
                  ? `${completionPeriods} months later`
                  : "of the following month"
              }
            />
          ) : (
            <Select
              ariaLabel={"Due month"}
              className={"w-[200px]"}
              value={form.dueMonthOffset}
              options={[
                { value: "same", label: "of the same month" },
                { value: "following", label: "of the following month" },
              ]}
              onChange={(dueMonthOffset) =>
                update({
                  dueMonthOffset: dueMonthOffset as "same" | "following",
                })
              }
            />
          )}
        </div>
      );
    }

    if (form.frequency === "quarterly") {
      return (
        <div className={"flex flex-wrap items-center gap-1.5"}>
          <span className={classNames(INLINE_TEXT_CLASSES, "shrink-0")}>
            on the
          </span>
          <Select
            ariaLabel={"Due day"}
            className={"w-[88px] shrink-0"}
            value={form.dueDay}
            options={dayOptions}
            onChange={(dueDay) => update({ dueDay })}
          />
          <span className={classNames(INLINE_TEXT_CLASSES, "shrink-0")}>
            of the
          </span>
          <Select
            ariaLabel={"Due month of the quarter"}
            className={"w-[104px] shrink-0"}
            value={form.dueQuarterMonth}
            options={QUARTER_MONTH_OPTIONS}
            onChange={(dueQuarterMonth) => update({ dueQuarterMonth })}
          />
          <FixedSelect
            ariaLabel={"Due quarter"}
            className={"min-w-[190px] flex-1"}
            label={
              isCompletion ? "of the following quarter" : "of the same quarter"
            }
          />
        </div>
      );
    }

    return (
      <div className={"flex flex-wrap items-center gap-2"}>
        <Select
          ariaLabel={"Due month"}
          className={"w-[140px]"}
          value={form.dueMonth}
          options={monthOptions}
          onChange={(dueMonth) => update({ dueMonth })}
        />
        <Select
          ariaLabel={"Due day"}
          className={"w-24"}
          value={form.dueDay}
          options={dayOptions}
          onChange={(dueDay) => update({ dueDay })}
        />
        {isCompletion ? (
          <FixedSelect
            ariaLabel={"Due year"}
            className={"w-[170px]"}
            label={"of the following year"}
          />
        ) : (
          <Select
            ariaLabel={"Due year"}
            className={"w-[170px]"}
            value={form.dueYearOffset}
            options={[
              { value: "same", label: "of the same year" },
              { value: "following", label: "of the following year" },
            ]}
            onChange={(dueYearOffset) =>
              update({ dueYearOffset: dueYearOffset as "same" | "following" })
            }
          />
        )}
      </div>
    );
  };

  // A due date before the start date is explained in a message when saving.
  const isInvalidDueDate = !result.ok && !!result.isInvalidDueDate;
  const error = !result.ok && !isInvalidDueDate ? result.error : null;

  // A plain button, not a <form>: this modal is also rendered inside the
  // work-details form, and a nested form would submit the page instead.
  const handleSave = () => {
    if (isLoading) return;
    if (!isRepeating) onClose();
    else if (result.ok) onSave(result.payload, form);
    else if (isInvalidDueDate) setIsInvalidDueDateOpen(true);
  };

  return (
    <div className={"modal modal-open font-Manrope"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Recurring work"}
        className={
          "modal-box flex max-h-[calc(100vh-48px)] w-[620px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "flex items-center justify-between border-b border-[#EDEFF3] px-[22px] pb-3.5 pt-4"
          }>
          <div
            className={
              "flex items-center gap-[9px] text-base font-extrabold text-[#1F2433]"
            }>
            <span
              className={
                "flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-[#F0EDFF] text-[#6B4EFF]"
              }>
              <LuRepeat size={16} />
            </span>
            Recurring work
            <InfoTip
              tone={"purple"}
              placement={"bottom"}
              text={"Set how often this work repeats and when it ends"}
            />
          </div>
          <button
            type={"button"}
            aria-label={"Close recurring work"}
            onClick={onClose}
            className={"text-[#A6ABBA] transition-colors hover:text-[#1F2433]"}>
            <LuX size={18} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-[22px] pb-5 pt-4"}>
          {isEditing && (
            <div
              className={
                "mb-4 rounded-lg border border-[#E6E0FF] bg-[#F6F4FF] px-3 py-2 text-[13px] text-[#4A5568]"
              }>
              Changes apply to work created from now on. Work items that already
              exist stay as they are.
            </div>
          )}
          <Row label={"Repeats"} first>
            <Select
              large
              ariaLabel={"Repeats"}
              value={form.frequency}
              options={
                // Ending a schedule is done with "Stop recurring".
                isEditing
                  ? FREQUENCY_OPTIONS.filter(
                      (option) => option.value !== "never",
                    )
                  : FREQUENCY_OPTIONS
              }
              onChange={(frequency) => update({ frequency })}
            />
          </Row>

          {form.frequency === "monthly" && (
            <Row label={"Every"}>
              <div className={"flex items-center gap-2"}>
                <Select
                  ariaLabel={"Every"}
                  className={"w-[74px]"}
                  value={form.every}
                  options={MONTHLY_EVERY_OPTIONS.map((value) => ({
                    value,
                    label: String(value),
                  }))}
                  onChange={(every) => update({ every })}
                />
                <span className={INLINE_TEXT_CLASSES}>month(s)</span>
              </div>
            </Row>
          )}

          {form.frequency === "quarterly" && (
            <Row label={"Fiscal year"}>
              <div className={"flex items-center gap-2"}>
                <span className={INLINE_TEXT_CLASSES}>begins in</span>
                <Select
                  ariaLabel={"Fiscal year begins in"}
                  className={"w-[140px]"}
                  value={form.fiscalStartMonth}
                  options={monthOptions}
                  onChange={(fiscalStartMonth) => update({ fiscalStartMonth })}
                />
              </div>
            </Row>
          )}

          {isRepeating && (
            <>
              <Row
                label={"Create work"}
                info={"This will also be the start date."}>
                <div
                  role={"radiogroup"}
                  aria-label={"Create work"}
                  className={"grid grid-cols-3 gap-2"}>
                  {CREATE_MODES.map((mode) => {
                    const isSelected = form.createMode === mode.value;
                    return (
                      <button
                        key={mode.value}
                        type={"button"}
                        role={"radio"}
                        aria-checked={isSelected}
                        onClick={() => update({ createMode: mode.value })}
                        className={classNames(
                          "relative flex h-20 flex-col items-center justify-center gap-1.5 rounded-[10px] border px-2 py-2.5 transition-colors",
                          isSelected
                            ? "border-[#6B4EFF] bg-[#FAF8FF]"
                            : "border-[#DDE1E8] bg-white hover:border-[#B9AEFF]",
                        )}>
                        {isSelected && (
                          <span
                            className={
                              "absolute left-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[#6B4EFF] text-white"
                            }>
                            <LuCheck size={10} strokeWidth={3.5} />
                          </span>
                        )}
                        <span
                          className={
                            "flex h-8 w-8 items-center justify-center rounded-full bg-[#EDE9FF] text-[#6B4EFF]"
                          }>
                          {mode.icon}
                        </span>
                        <span
                          className={"text-xs font-semibold text-[#3A4256]"}>
                          {mode.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {renderCreateDetails()}
              </Row>

              <Row label={"Due"}>{renderDue()}</Row>

              <Row label={"Repeat until"}>
                <div
                  className={
                    "flex h-[38px] w-[175px] items-center justify-between gap-2 overflow-hidden rounded-lg border border-[#DCE2EA] bg-white pl-3 pr-2.5 text-[13.5px]"
                  }>
                  <div className={"min-w-0 flex-1 overflow-hidden"}>
                    <StyledDatePicker
                      value={form.repeatUntil}
                      onChange={(repeatUntil) => {
                        setIsRepeatUntilEdited(true);
                        update({ repeatUntil });
                      }}
                      format={"MMM dd, yyyy"}
                      acceptTypedDates
                      styleType={"ghost"}
                      width={"120px"}
                    />
                  </div>
                  <LuCalendar size={15} className={"shrink-0 text-[#9097A8]"} />
                </div>
              </Row>

              {result.ok && result.copies !== undefined && (
                <div className={"mt-1 text-[12.5px] text-[#6B7185]"}>
                  Saving creates {result.copies} work item
                  {result.copies === 1 ? "" : "s"} at once.
                </div>
              )}
              {error && (
                <div
                  role={"alert"}
                  className={
                    "mt-1 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
                  }>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div
          className={
            "flex flex-none items-center justify-end gap-2.5 border-t border-[#EDEFF3] px-[22px] py-3.5"
          }>
          <Button
            type={"button"}
            variant={"outline"}
            autoWidth
            disabled={isLoading}
            className={
              "!h-[35px] !rounded-lg !border-[#DCE2EA] px-[22px] !text-[13.5px] !font-bold !text-[#3A4256]"
            }
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={"button"}
            onClick={handleSave}
            autoWidth
            loading={isLoading}
            disabled={isRepeating && !result.ok && !isInvalidDueDate}
            className={
              "!h-[35px] !rounded-lg !border-[#6B4EFF] !bg-[#6B4EFF] px-6 !text-[13.5px] !font-bold hover:!border-[#5A3EEB] hover:!bg-[#5A3EEB] disabled:!border-[#C3C8D4] disabled:!bg-[#C3C8D4]"
            }>
            Save
          </Button>
        </div>
      </div>
      {isInvalidDueDateOpen && (
        <div
          className={
            "fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(16,24,40,0.35)] px-4"
          }>
          <div
            role={"alertdialog"}
            aria-modal={"true"}
            aria-labelledby={"invalid-due-date-title"}
            className={
              "w-[380px] max-w-full rounded-[14px] bg-white px-6 pb-5 pt-6 text-center shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
            }>
            <div
              className={
                "mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#FDECEC] text-[#D14343]"
              }>
              <LuCalendar size={18} />
            </div>
            <div
              id={"invalid-due-date-title"}
              className={"text-base font-extrabold text-[#1F2433]"}>
              Invalid Due Date
            </div>
            <p className={"mt-2 text-[13.5px] leading-relaxed text-[#5B6678]"}>
              The due date can&apos;t be earlier than the start date. Please
              pick a due date on or after the start date.
            </p>
            <button
              type={"button"}
              autoFocus
              onClick={() => setIsInvalidDueDateOpen(false)}
              className={
                "mt-5 h-[35px] w-full rounded-lg bg-[#6B4EFF] text-[13.5px] font-bold text-white hover:bg-[#5A3EEB]"
              }>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
